// /user-chat ページ: コンシェルジュとのチャット UI (Figma node 479-65027)
// 静的スタブ: 後続でリアルタイム通信層とメッセージ取得を差し替え予定
import React, { useEffect, useState } from 'react';

import { Footer } from '../../components/user/Footer';
import { Header } from '../../components/user/Header';
import { apiClient } from '../../utils/apiClient';

interface ChatMessage {
  id: string;
  sender: 'concierge' | 'user';
  text: string;
}

// ChannelMessagesResponse に準拠した簡易型
interface ChannelMessagesResponse {
  ChannelArn: string;
  ChannelMessages: Array<{
    Content: string;
    MessageId: string;
    MessageAttributes?: Record<string, { StringValues?: string[] }>;
    Sender?: { Arn?: string; Name?: string };
    CreatedTimestamp?: number;
  }>;
  NextToken?: string;
  SubChannelId?: string;
}

const UserChatPage: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = (await apiClient('/api/chat/sample')) as ChannelMessagesResponse;
        // CreatedTimestamp 昇順に並べ替え
        const sorted = [...(data.ChannelMessages || [])].sort((a, b) => {
          const ta =
            typeof a.CreatedTimestamp === 'number' ? a.CreatedTimestamp : Number.MAX_SAFE_INTEGER;
          const tb =
            typeof b.CreatedTimestamp === 'number' ? b.CreatedTimestamp : Number.MAX_SAFE_INTEGER;
          return ta - tb;
        });
        const mapped: ChatMessage[] = sorted.map((m) => {
          // senderType は MessageAttributes['senderType'].StringValues[0] を想定
          const attrType = m.MessageAttributes?.senderType?.StringValues?.[0];
          const sender: 'concierge' | 'user' = attrType === 'user' ? 'user' : 'concierge';
          return {
            id: m.MessageId || crypto.randomUUID(),
            sender,
            text: m.Content || '',
          };
        });
        if (mounted) setMessages(mapped);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        if (mounted) setError(msg);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || sending) return;
    setSending(true);
    // 疑似送信遅延
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        { id: `m${prev.length + 1}`, sender: 'user', text: trimmed },
      ]);
      setInput('');
      setSending(false);
    }, 300);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1 flex flex-col px-[40px] pt-[30px] pb-[140px] gap-[40px] max-w-[640px] w-full mx-auto">
        <div className="flex items-center gap-[20px]">
          <button
            type="button"
            aria-label="戻る"
            className="text-[24px] font-bold px-2 py-1 rounded hover:bg-gray-100"
            onClick={() => window.history.back()}
          >
            ＜
          </button>
          <h1 className="text-[24px] font-bold">コンシェルジュ</h1>
        </div>
        {loading && <div className="text-gray-600">履歴を読み込み中...</div>}
        {error && <div className="text-red-600">履歴取得に失敗しました: {error}</div>}
        <div className="flex flex-col gap-[30px]" aria-live="polite">
          {messages.map((m) => {
            const concierge = m.sender === 'concierge';
            return (
              <div
                key={m.id}
                className={[
                  'flex w-full gap-[10px]',
                  concierge ? 'justify-start items-start' : 'justify-end items-center',
                ].join(' ')}
              >
                {concierge && (
                  <div className="w-[40px] h-[40px] rounded-full bg-gray-300 shrink-0" />
                )}
                <div
                  className={[
                    'border border-black rounded-[30px] box-border',
                    concierge
                      ? 'px-[30px] py-[30px] max-w-[440px]'
                      : 'px-[30px] py-[10px] rounded-[80px] max-w-[360px]',
                    'bg-white flex items-center',
                  ].join(' ')}
                >
                  <p className="text-[18px] whitespace-pre-wrap leading-[30px]">{m.text}</p>
                </div>
                {!concierge && (
                  <div className="w-[40px] h-[40px] rounded-full bg-gray-300 shrink-0" />
                )}
              </div>
            );
          })}
        </div>
        {/* 入力エリア */}
        <div className="mt-auto" aria-label="メッセージ送信フォーム">
          <div className="border border-black rounded-[70px] flex items-center gap-[10px] px-[20px] py-[10px]">
            <input
              type="text"
              placeholder="メッセージを入力"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="flex-1 outline-none text-[16px]"
              aria-label="メッセージ入力"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !input.trim()}
              className={[
                'px-[20px] py-[10px] rounded-[70px] text-[18px] font-bold',
                sending || !input.trim()
                  ? 'bg-gray-300 text-white cursor-not-allowed'
                  : 'bg-[#ff7a7a] text-white hover:brightness-110',
                'transition-colors duration-150',
              ].join(' ')}
            >
              送信
            </button>
          </div>
        </div>
      </main>
      <Footer className="mt-auto" />
    </div>
  );
};

export default UserChatPage;
